import { useState, useCallback } from 'react';
import {
  Table,
  Space,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
  Typography,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  useWisecureQuery,
  useWisecureMutation,
  useInvalidateQueries,
} from '@wisecure/api-client/hooks';
import { queryKeys } from '@/api/query-keys';
import { PageHeader } from '@/components/PageHeader';
import type { PaginatedResponse } from '@/types/dashboard';
import type {
  BrandResponse,
  CreateBrandDto,
  UpdateBrandDto,
} from '@wisecure/api-client';

const { Text } = Typography;

type BrandCategory =
  | 'ALARME_INTRUSION'
  | 'VIDEO_SURVEILLANCE'
  | 'CONTROLE_D_ACCES'
  | 'DOMOTIQUE'
  | 'ALARME_INCENDIE';

const CATEGORY_OPTIONS = [
  { value: '', label: 'Toutes' },
  { value: 'ALARME_INTRUSION', label: 'Alarme intrusion' },
  { value: 'VIDEO_SURVEILLANCE', label: 'Vidéo surveillance' },
  { value: 'CONTROLE_D_ACCES', label: "Contrôle d'accès" },
  { value: 'DOMOTIQUE', label: 'Domotique' },
  { value: 'ALARME_INCENDIE', label: 'Alarme incendie' },
];

const CATEGORY_COLORS: Record<BrandCategory, string> = {
  ALARME_INTRUSION: 'red',
  VIDEO_SURVEILLANCE: 'blue',
  CONTROLE_D_ACCES: 'green',
  DOMOTIQUE: 'purple',
  ALARME_INCENDIE: 'orange',
};

const CATEGORY_LABELS: Record<BrandCategory, string> = {
  ALARME_INTRUSION: 'Alarme intrusion',
  VIDEO_SURVEILLANCE: 'Vidéo surveillance',
  CONTROLE_D_ACCES: "Contrôle d'accès",
  DOMOTIQUE: 'Domotique',
  ALARME_INCENDIE: 'Alarme incendie',
};

export function BrandsPage() {
  const invalidate = useInvalidateQueries();
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState<BrandResponse | null>(null);
  const [form] = Form.useForm();

  const filters = {
    ...(categoryFilter ? { category: categoryFilter as BrandCategory } : {}),
    ...(search ? { search } : {}),
  };

  const { data: brandsResponse, isLoading } = useWisecureQuery<
    PaginatedResponse<BrandResponse>
  >(
    queryKeys.brands(filters as Record<string, unknown>),
    (client) =>
      client.brands.brandsControllerFindAll({
        limit: 100,
        ...filters,
      }) as Promise<PaginatedResponse<BrandResponse>>,
  );

  const createMutation = useWisecureMutation(
    (client, values: CreateBrandDto) =>
      client.brands.brandsControllerCreate({ requestBody: values }),
    {
      onSuccess: () => {
        message.success('Marque créée');
        invalidate(queryKeys.brands());
        handleClose();
      },
      onError: (error: { body?: { message?: string }; message?: string }) => {
        const msg = error?.body?.message ?? error?.message;
        if (msg?.includes('already exists')) {
          message.error('Une marque avec ce label existe déjà dans cette catégorie');
        } else {
          message.error('Erreur lors de la création de la marque');
        }
      },
    },
  );

  const updateMutation = useWisecureMutation(
    (client, { id, values }: { id: string; values: UpdateBrandDto }) =>
      client.brands.brandsControllerUpdate({ id, requestBody: values }),
    {
      onSuccess: () => {
        message.success('Marque mise à jour');
        invalidate(queryKeys.brands());
        handleClose();
      },
      onError: () => {
        message.error('Erreur lors de la mise à jour de la marque');
      },
    },
  );

  const toggleEnabledMutation = useWisecureMutation(
    (client, { id, enabled }: { id: string; enabled: boolean }) =>
      client.brands.brandsControllerUpdate({
        id,
        requestBody: { enabled },
      }),
    {
      onSuccess: () => {
        message.success('Statut mis à jour');
        invalidate(queryKeys.brands());
      },
      onError: () => {
        message.error('Erreur lors de la mise à jour du statut');
      },
    },
  );

  const deleteMutation = useWisecureMutation<unknown, string>(
    (client, id) => client.brands.brandsControllerRemove({ id }),
    {
      onSuccess: () => {
        message.success('Marque supprimée');
        invalidate(queryKeys.brands());
      },
      onError: () => {
        message.error('Erreur lors de la suppression');
      },
    },
  );

  const handleClose = useCallback(() => {
    setModalOpen(false);
    setEditingBrand(null);
    form.resetFields();
  }, [form]);

  const handleOpenCreate = useCallback(() => {
    setEditingBrand(null);
    form.resetFields();
    form.setFieldsValue({ enabled: true });
    setModalOpen(true);
  }, [form]);

  const handleOpenEdit = useCallback(
    (brand: BrandResponse) => {
      setEditingBrand(brand);
      form.setFieldsValue({
        label: brand.label,
        logo: brand.logo,
        category: brand.category,
        enabled: brand.enabled,
      });
      setModalOpen(true);
    },
    [form],
  );

  const handleSubmit = useCallback(() => {
    form.validateFields().then((values) => {
      if (editingBrand) {
        updateMutation.mutate({ id: editingBrand.id, values });
      } else {
        createMutation.mutate(values as CreateBrandDto);
      }
    });
  }, [form, editingBrand, createMutation, updateMutation]);

  const columns = [
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      render: (label: string) => (
        <Text strong style={{ fontSize: 14 }}>
          {label}
        </Text>
      ),
    },
    {
      title: 'Catégorie',
      dataIndex: 'category',
      key: 'category',
      render: (category: BrandCategory) => (
        <Tag color={CATEGORY_COLORS[category]}>
          {CATEGORY_LABELS[category]}
        </Tag>
      ),
    },
    {
      title: 'Logo',
      dataIndex: 'logo',
      key: 'logo',
      render: (logo: Record<string, unknown> | undefined) =>
        logo ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Configuré
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            —
          </Text>
        ),
    },
    {
      title: 'Activé',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (enabled: boolean, record: BrandResponse) => (
        <Switch
          size="small"
          checked={enabled}
          loading={toggleEnabledMutation.isPending}
          onChange={(checked) =>
            toggleEnabledMutation.mutate({ id: record.id, enabled: checked })
          }
        />
      ),
    },
    {
      title: 'Créé le',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('fr-FR'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: unknown, record: BrandResponse) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenEdit(record)}
          />
          <Popconfirm
            title="Supprimer la marque ?"
            onConfirm={() => deleteMutation.mutate(record.id)}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const data = (brandsResponse?.data ?? []) as BrandResponse[];
  const total = brandsResponse?.total ?? 0;

  return (
    <div>
      <PageHeader
        title="Marques"
        subtitle={`${total} marque(s) enregistrée(s)`}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
            Ajouter une marque
          </Button>
        }
      />

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          value={categoryFilter}
          onChange={setCategoryFilter}
          options={CATEGORY_OPTIONS}
          style={{ width: 200 }}
          placeholder="Filtrer par catégorie"
        />
        <Input.Search
          placeholder="Rechercher par label..."
          allowClear
          onSearch={(value) => setSearch(value)}
          style={{ width: 250 }}
        />
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        loading={isLoading}
        rowKey="id"
        pagination={false}
      />

      <Modal
        open={modalOpen}
        onCancel={handleClose}
        title={editingBrand ? 'Modifier la marque' : 'Créer une marque'}
        onOk={handleSubmit}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ enabled: true }}
        >
          <Form.Item
            name="label"
            label="Label"
            rules={[{ required: true, message: 'Le label est requis' }]}
          >
            <Input placeholder="Ex: Hikvision" />
          </Form.Item>

          <Form.Item
            name="logo"
            label="Logo (URL)"
          >
            <Input placeholder="https://example.com/logo.png" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Catégorie"
            rules={[{ required: true, message: 'La catégorie est requise' }]}
          >
            <Select
              placeholder="Sélectionner une catégorie"
              options={CATEGORY_OPTIONS.filter((o) => o.value !== '')}
            />
          </Form.Item>

          <Form.Item
            name="enabled"
            label="Activé"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
